import * as SmartyStreetsSDK from 'smartystreets-javascript-sdk'
import { updateRequestWhenAvailable } from 'mapi-js'

/**
 * @classdesc Address Qualification
 * method that sends data to AQS and prioritizes call by updating MAPI request
 */
class AddressQualification {
  /**
   * @param {string} addressQualificationCode - code used to send to the AQS qualifications endpoint, i.e. att-saraplus
   * @param {string} smartyStreetsWebsiteKey - string of numbers that represents the key Smarty Streets SDK needs
   */
  constructor({ addressQualificationCode, smartyStreetsWebsiteKey }) {
    this.addressQualificationCode = addressQualificationCode

    this.SmartyStreetsCore = SmartyStreetsSDK.core
    this.SmartyStreetsAutoLookup = SmartyStreetsSDK.usAutocomplete.Lookup
    this.SmartyStreetsStreetLookup = SmartyStreetsSDK.usStreet.Lookup
    this.WebsiteKey = smartyStreetsWebsiteKey
    this.credentials = new this.SmartyStreetsCore.SharedCredentials(
      this.WebsiteKey
    )
    this.AutoClient = this.SmartyStreetsCore.buildClient.usAutocomplete(
      this.credentials
    )
    this.AddressClient = this.SmartyStreetsCore.buildClient.usStreet(
      this.credentials
    )
  }

  /**
   * Parses out a given address and qualifies it within the AQS engineering system
   * It then updates the MAPI request with the request ID generated
   * @return {string} - returns a string of the AQS request ID
   */
  sendQualificationData(address, zipCode, requestID) {
    return new Promise((resolve, reject) => {
      const lookup = new this.SmartyStreetsStreetLookup()
      lookup.street = address

      this.AddressClient.send(lookup)
        .then((response) => {
          let addressData = {}

          if (typeof response.lookups[0].result[0] !== 'undefined') {
            const { components } = response.lookups[0].result[0]

            addressData = {
              line1: response.lookups[0].result[0].deliveryLine1,
              locality: components.cityName,
              administrative_area: components.state,
              postal_code: components.zipCode,
              country_code: 'US',
            }
          } else {
            addressData = {
              line1: response.lookups[0].street,
              locality: '',
              administrative_area: '',
              postal_code: '',
              country_code: 'US',
            }

            // If smarty streets doesn't come back with a zip code, we use the zip passed in.
            // We do this because the AQS endpoint requires it for a job to be
            // completed. It will submit without a zipcode but will stay in pending status forever.
            if (zipCode !== null) {
              addressData.postal_code = zipCode
            }
          }

          const body = {
            address: addressData,
            requests: [
              {
                type: this.addressQualificationCode,
              },
            ],
            interactions: [
              {
                interaction_type: 'mapi',
                interaction_id: requestID,
              },
            ],
          }

          fetch('https://aqs.clear.link/qualifications/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
          })
            .then((res) => (res.ok ? res.json() : {}))
            .then((data) => {
              updateRequestWhenAvailable({
                aqs_requests: {
                  aqs_request_id: data.request_id,
                },
              })

              resolve({
                addressData,
                aqsRequestID: data.request_id,
              })
            })
            .catch((err) => {
              reject(
                new Error(
                  `Leshen:UI:AddressQualification - error hitting AQS qualifications: ${err}`
                )
              )
            })
        })
        .catch((err) =>
          reject(
            new Error(
              `Leshen:UI:AddressQualification - error in address qualification: ${err}`
            )
          )
        )
    })
  }

  checkForService(address, requestID) {
    return new Promise((resolve, reject) => {
      this.sendQualificationData(address, null, requestID).then(
        (aqsResponse) => {
          let count = 0

          const intervalID = setInterval(() => {
            if (count === 20) {
              reject(
                new Error(
                  'Leshen:UI:AddressQualification - hit endpoint 20 times with no data resolved'
                )
              )
            }

            fetch(
              `https://aqs.clear.link/requests/${aqsResponse.aqsRequestID}/responses`
            )
              .then((res) => (res.ok ? res.json() : {}))
              .then((data) => {
                if (!data?.responses?.lenth) return
                data.responses.forEach((currentResponse) => {
                  if (currentResponse.status === 'Completed') {
                    clearInterval(intervalID)
                    resolve({
                      address: aqsResponse.addressData,
                      serviceData: data.responses[0],
                    })
                  }
                })
              })
              .catch((err) =>
                reject(
                  new Error(
                    `Leshen:UI:AddressQualification - error in getting address qualification packages: ${err}`
                  )
                )
              )

            count += 1
          }, 1000)
        }
      )
    })
  }
}

export default AddressQualification
